import { useEffect, useRef, useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import httpclient from "../../../Utils";
import CustomLoadingOverlay from "../../CustomLoadingOverlay";
import OrderAuditTrialModal from "../OrderAuditTrialModal";

import OrderDetailsForm from "./OrderDetailsForm";
import OrderLineItemsTable from "./OrderLineItemsTable";
import {
  ReleaseOrderAction,
  AssignToPickerPackerAction,
  HoldOrderAction,
  CancelOrderAction,
  CompleteOrderAction,
  RefetchFromERPAction,
} from "./ConfirmationDialogs";
import StatusAlert from "./StatusAlert";
import MissingInfoAlert from "./MissingInfoAlert";

const OrderDetailsModal = ({ open, handleClose, selectedOrder }) => {
  const [formDetails, setFormDetails] = useState(null);
  const [priority, setPriority] = useState("");
  const [picker, setPicker] = useState("");
  const [notifyPicker, setNotifyPicker] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  // State for pagination


  // Refs for confirmation dialogs
  const releaseOrderActionRef = useRef(null);
  const assignToPickerPackerActionRef = useRef(null);
  const holdOrderActionRef = useRef(null);
  const cancelOrderActionRef = useRef(null);
  const completeOrderActionRef = useRef(null);
  const refetchFromERPActionRef = useRef(null);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: tokenRefresh } = useTokenRefresh();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      const API_URL = `orders/${selectedOrder?.job_id}/details`;

      try {
        const res = await httpclient.get(API_URL);
        const { data } = res;

        if (data.status === 200) {
          setFormDetails(data.data);
          setRefresh(false);
        } else {
          showNotification("error", data.msg);
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          tokenRefresh();
        } else {
          handleApiError(error);
        }
      } finally {
        setLoading(false);
      }
    };

    if (selectedOrder?.job_id) {
      fetchOrderDetails();
    }
  }, [selectedOrder, refresh]);



  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "priority") setPriority(value);
    else if (name === "picker") setPicker(value);
    setFormDetails({ ...formDetails, [name]: value });
  };

  const handleUpdate = async () => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("priority", priority || "");
      formData.append("picker", picker || "");
      formData.append("order_alert_message", notifyPicker ? "1" : "0");
      if (notifyPicker) {
        formData.append("custom_message", customMessage);
      }

      const endpoint = `/orders/${formDetails?.job_id}/update`;
      const res = await httpclient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { data } = res;
      if (data.status === 200) {
        showNotification("success", data.message);
        setRefresh(true);
        setPriority("");
        setPicker("");
        setCustomMessage("");
        setNotifyPicker(true);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    setRefresh(true);
  };

  const handleGenerateInvoiceNumber = async () => {
    try {
      setIsGeneratingInvoice(true);
      const endpoint = `/orders/${formDetails?.job_id}/fetch-invoice-number`;
      const res = await httpclient.get(endpoint);
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        setRefresh(true);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleActionsMenuClick = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const openActionsMenu = Boolean(actionsMenuAnchorEl);

  // Array of available actions to easily count and render them.
  const availableActions = useMemo(() => {
    const actions = [];
    if (!formDetails) return actions;

    if (formDetails.order_release_button_enabled === 1) {
      actions.push({
        label: "Release Order",
        handler: () => releaseOrderActionRef.current?.openDialog(),
      });
    }
    if (formDetails.assign_same_picker_packer_button_enabled === 1) {
      actions.push({
        label: "Release to same Picker/Packer",
        handler: () => assignToPickerPackerActionRef.current?.openDialog(),
      });
    }
    if (formDetails.order_hold_button_enabled === 1) {
      actions.push({
        label: "Hold Order",
        handler: () => holdOrderActionRef.current?.openDialog(),
      });
    }
    if (formDetails.mark_as_complete_button === 1) {
      actions.push({
        label: "Mark as Completed",
        handler: () => completeOrderActionRef.current?.openDialog(),
      });
    }
    if (formDetails.is_refetch_order === 1) {
      actions.push({
        label: "Refetch from ERP",
        handler: () => refetchFromERPActionRef.current?.openDialog(),
      });
    }
    if (formDetails.order_cancel_button_enabled === 1) {
      actions.push({
        label: "Cancel Order",
        handler: () => cancelOrderActionRef.current?.openDialog(),
      });
    }
    return actions;
  }, [formDetails]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 4, pb: 2 }}>
          <StatusAlert formDetails={formDetails} />
          {formDetails?.order_alert_message ? (
            <MissingInfoAlert formDetails={formDetails} />
          ) : null}
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Lexend",
              fontSize: "20px",
              mb: 3,
              fontWeight: 500,
              color: "383E49",
            }}
          >
            Order Statuses
          </Typography>

          {loading ? (
            <CustomLoadingOverlay loadingText="Loading. Please wait..." />
          ) : (
            <>
              <OrderDetailsForm
                formDetails={formDetails}
                handleFormChange={handleFormChange}
                notifyPicker={notifyPicker}
                setNotifyPicker={setNotifyPicker}
                customMessage={customMessage}
                setCustomMessage={setCustomMessage}
              />
              <OrderLineItemsTable
                jobId={selectedOrder?.job_id}
                onRefresh={handleRefresh}
              />
            </>
          )}
        </DialogContent>
        {!loading && (
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left side buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => setOpenTrialModal(true)}
              >
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  Audit Trail
                </Typography>
              </Button>
              {formDetails?.is_invoice_number_generate_enabled === 1 && (
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleGenerateInvoiceNumber}
                  disabled={isGeneratingInvoice}
                  sx={{ ml: 1 }}
                >
                  {isGeneratingInvoice ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ color: "#fff", mr: 1 }}
                      />{" "}
                      <Typography variant="body2" sx={{ color: "#fff" }}>
                        Syncing...
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      Sync Invoice Number from ERP
                    </Typography>
                  )}
                </Button>
              )}
            </Box>

            {/* Right side buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* If only one action is available, show it as a direct button with conditional styling. */}
              {availableActions.length === 1 && (
                <Button
                  variant={
                    availableActions[0].label === "Mark as Completed"
                      ? "contained"
                      : "outlined"
                  }
                  onClick={availableActions[0].handler}
                  sx={
                    availableActions[0].label === "Mark as Completed"
                      ? {
                          backgroundColor: "#4caf50",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "#43a047",
                          },
                        }
                      : {
                          borderColor: "#7152F3",
                          color: "#7152F3",
                        }
                  }
                >
                  {availableActions[0].label}
                </Button>
              )}

              {/* If more than one action is available, show the dropdown menu. */}
              {availableActions.length > 1 && (
                <>
                  <Button
                    aria-controls="order-actions-menu"
                    aria-haspopup="true"
                    aria-expanded={openActionsMenu ? "true" : undefined}
                    variant="outlined"
                    onClick={handleActionsMenuClick}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{ borderColor: "#7152F3", color: "#7152F3" }}
                  >
                    Order Actions
                  </Button>
                  <Menu
                    id="order-actions-menu"
                    anchorEl={actionsMenuAnchorEl}
                    open={openActionsMenu}
                    onClose={handleActionsMenuClose}
                    MenuListProps={{
                      "aria-labelledby": "order-actions-button",
                    }}
                  >
                    {availableActions.map((action) => (
                      <MenuItem
                        key={action.label}
                        onClick={() => {
                          action.handler();
                          handleActionsMenuClose();
                        }}
                      >
                        {action.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ borderRadius: 1, ml: 3 }}
              >
                Close
              </Button>
              {formDetails?.is_update_order ? (
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "#3FC28A",
                    "&:hover": { backgroundColor: "#3aa880" },
                  }}
                  onClick={handleUpdate}
                  disabled={!(priority || picker) || isSubmitting}
                >
                  {isSubmitting ? "Updating" : "Update"}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={{ marginLeft: "16px" }} />
                  )}
                </Button>
              ) : (
                ""
              )}
            </Box>
          </DialogActions>
        )}
      </Dialog>

      {formDetails?.job_id && (
        <>
          {formDetails?.order_release_button_enabled === 1 && (
            <ReleaseOrderAction
              ref={releaseOrderActionRef}
              jobId={formDetails.job_id}
              onRelease={handleRefresh}
            />
          )}
          {formDetails?.assign_same_picker_packer_button_enabled === 1 && (
            <AssignToPickerPackerAction
              ref={assignToPickerPackerActionRef}
              jobId={formDetails.job_id}
              onAssign={handleRefresh}
            />
          )}
          {formDetails?.order_hold_button_enabled === 1 && (
            <HoldOrderAction
              ref={holdOrderActionRef}
              jobId={formDetails.job_id}
              onHold={handleRefresh}
            />
          )}
          {formDetails?.mark_as_complete_button === 1 && (
            <CompleteOrderAction
              ref={completeOrderActionRef}
              jobId={formDetails.job_id}
              onComplete={handleRefresh}
            />
          )}
          {formDetails?.order_cancel_button_enabled === 1 && (
            <CancelOrderAction
              ref={cancelOrderActionRef}
              jobId={formDetails.job_id}
              onCancel={handleClose} // Cancelling order closes the modal
            />
          )}
          {formDetails?.is_refetch_order === 1 && (
            <RefetchFromERPAction
              ref={refetchFromERPActionRef}
              jobId={formDetails.job_id}
              onRefetch={handleRefresh}
            />
          )}
        </>
      )}

      {openTrialModal && (
        <OrderAuditTrialModal
          open={openTrialModal}
          handleClose={() => setOpenTrialModal(false)}
          jobId={formDetails?.job_id}
        />
      )}
    </>
  );
};

export default OrderDetailsModal;
